import * as XLSX from "xlsx";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    CloseCircleFilled,
    DatabaseOutlined,
    DownloadOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

// ─── Branches sélectionnables ──────────────────────────────────────────────

const BRANCHES = [
    {
        value: "TPV",
        label: "TPV — Transport Public de Voyageurs",
        description: "Bus (B), Autocar (A), Taxi / VLC (T) — catégorie déterminée par le nombre de places",
        color: "blue",
    },
    {
        value: "MOTO",
        label: "MOTO — Engins deux / trois roues et assimilés",
        description: "Catégorie M — motos, tricycles, et assimilés",
        color: "orange",
    },
    {
        value: "VT",
        label: "VT — Véhicules en Transit",
        description: "Catégorie V — véhicules en transit",
        color: "green",
    },
];

// ─── Labels ────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
    BDE:      { label: "BDE / BDA",        color: "orange", desc: "27 colonnes · .xls · header ligne ~6" },
    EFORCE:   { label: "BD-POOL eFORCE",   color: "blue",   desc: "54 colonnes · .xls · header ligne 1" },
    TEMPLATE: { label: "Template Émissions", color: "green", desc: "22 colonnes · .xlsx · header ligne 1-2" },
};

const CAT_LABELS = {
    T: "Taxi / VLC (< 10 places)",
    A: "Autocar (10 – 20 places)",
    B: "Bus (≥ 21 places)",
    M: "Moto / deux-trois roues",
    V: "Véhicule en Transit",
};

const OP_LABELS = {
    AN: "Affaire Nouvelle", RN: "Renouvellement", PR: "Prorogation",
    SP: "Suspension", RE: "Résiliation", RS: "Résiliation",
};

// ─── Colonnes table erreurs ────────────────────────────────────────────────

const erreurColumns = [
    { title: "Ligne", dataIndex: "ligne", key: "ligne", width: 65,
      render: v => <Text type="secondary">{v ?? "—"}</Text> },
    { title: "N° Police", dataIndex: "police", key: "police", width: 155,
      render: v => <Text code style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Avenant", dataIndex: "avenant", key: "avenant", width: 80,
      render: v => <Tag color="default" style={{ fontSize: 11 }}>{v || "—"}</Tag> },
    { title: "Assuré", dataIndex: "assure", key: "assure", width: 160,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Immat.", dataIndex: "immatriculation", key: "immatriculation", width: 110,
      render: v => <Text code style={{ fontSize: 11 }}>{v || "—"}</Text> },
    { title: "Marque", dataIndex: "marque", key: "marque", width: 100,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Cat.", dataIndex: "categorie", key: "categorie", width: 55,
      render: v => v && v !== "—" ? <Tag color="geekblue" style={{ fontSize: 11 }}>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: "Effet", dataIndex: "dateEffet", key: "dateEffet", width: 100,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Prime TTC", dataIndex: "primeTotale", key: "primeTotale", width: 110,
      render: v => <Text style={{ fontSize: 12 }}>{v != null ? new Intl.NumberFormat("fr-FR").format(Math.round(v)) : "—"}</Text> },
    { title: "Attestation", dataIndex: "attestation", key: "attestation", width: 130,
      render: v => <Text style={{ fontSize: 11 }}>{v || "—"}</Text> },
    { title: "Cause du rejet", dataIndex: "erreur", key: "erreur", minWidth: 200,
      render: v => <Text type="danger" style={{ fontSize: 12 }}>{v || "—"}</Text> },
];

// ─── Colonnes table preview lignes ────────────────────────────────────────

const previewLignesColumns = [
    {
        title: "", key: "s", width: 36, fixed: "left",
        render: (_, r) => r._dejaImporte
            ? <Tooltip title="Déjà présent en base — sera ignoré à l'import">
                <DatabaseOutlined style={{ color: "#9333ea" }} />
              </Tooltip>
            : r._hasWarning
                ? <Tooltip title={(r._warnings || []).join(" · ")}>
                    <WarningOutlined style={{ color: "#f97316" }} />
                  </Tooltip>
                : <CheckCircleOutlined style={{ color: "#1a6b3c" }} />,
    },
    { title: "Ligne", dataIndex: "ligne", key: "ligne", width: 65,
      render: v => <Text type="secondary" style={{ fontSize: 11 }}>L{v ?? "?"}</Text> },
    { title: "N° Police", dataIndex: "police", key: "police", width: 165, fixed: "left",
      render: (v, r) => (
          <Space size={4}>
              <Text code style={{ fontSize: 11, fontWeight: 700 }}>{v || "—"}</Text>
              {r._dejaImporte && (
                  <Tooltip title="Déjà présent en base — ignoré à l'import">
                      <Tag color="purple" style={{ fontSize: 9, margin: 0 }}>déjà</Tag>
                  </Tooltip>
              )}
          </Space>
      ) },
    { title: "Avenant", dataIndex: "avenant", key: "avenant", width: 80,
      render: v => <Tag style={{ fontSize: 10 }}>{v || "—"}</Tag> },
    { title: "Assuré", dataIndex: "assure", key: "assure", ellipsis: true, width: 160,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Immat.", dataIndex: "immatriculation", key: "immatriculation", width: 115,
      render: v => (!v || v === "—")
          ? <Tag color="red" style={{ fontSize: 10 }}>Manquant</Tag>
          : <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: "Marque", dataIndex: "marque", key: "marque", width: 110,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Cat.", dataIndex: "categorie", key: "categorie", width: 55, align: "center",
      render: v => v && v !== "—"
          ? <Tag color="geekblue" style={{ fontSize: 10 }}>{v}</Tag>
          : <Text type="secondary">—</Text> },
    { title: "Engins", dataIndex: "nbEngins", key: "nbEngins", width: 65, align: "center",
      render: v => v > 1 ? <Tag color="orange" style={{ fontSize: 10 }}>{v}</Tag> : v },
    { title: "Effet", dataIndex: "dateEffet", key: "dateEffet", width: 100,
      render: v => v
          ? <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString("fr-FR")}</Text>
          : <Tag color="red" style={{ fontSize: 10 }}>Manquant</Tag> },
    { title: "Échéance", dataIndex: "dateEcheance", key: "dateEcheance", width: 100,
      render: v => v
          ? <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString("fr-FR")}</Text>
          : <Tag color="red" style={{ fontSize: 10 }}>Manquant</Tag> },
    { title: "Prime TTC (FCFA)", dataIndex: "primeTotale", key: "primeTotale", width: 145, align: "right",
      render: v => <Text strong style={{ fontSize: 12, color: "#166534" }}>
          {v != null ? new Intl.NumberFormat("fr-FR").format(Math.round(v)) : "—"}
      </Text> },
    { title: "Attestation", dataIndex: "attestation", key: "attestation", width: 130,
      render: v => <Text style={{ fontSize: 11 }}>{v || "—"}</Text> },
];

// ─── Helpers affichage ─────────────────────────────────────────────────────

const formatMontant = (v) =>
    v == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "decimal" }).format(Math.round(v)) + " FCFA";

const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleDateString("fr-FR");
};

// ─── Composant carte de preview ────────────────────────────────────────────

const PreviewCard = ({ preview }) => {
    if (!preview.ok) {
        return (
            <Alert type="error" showIcon
                message={`Erreur — ${preview.fichier}`}
                description={preview.messageErreur}
                style={{ marginBottom: 12 }}
            />
        );
    }

    const ops  = preview.repartitionOperations || {};
    const cats = preview.repartitionCategories || {};
    const fmt  = FORMAT_LABELS[preview.formatDetecte] || {};

    return (
        <Card
            size="small"
            style={{ marginBottom: 16, borderLeft: "4px solid #242166" }}
            title={
                <Space wrap>
                    <FileExcelOutlined style={{ color: "#217346" }} />
                    <Text strong>{preview.fichier}</Text>
                    {fmt.label && <Tag color={fmt.color}>{fmt.label}</Tag>}
                    {fmt.desc  && <Text type="secondary" style={{ fontSize: 11 }}>{fmt.desc}</Text>}
                    {preview.formatDetecte === "EFORCE" && (
                        <Tag color="cyan" style={{ fontSize: 11 }}>Multibranches — catégories du fichier</Tag>
                    )}
                    <Tag color="default">{(preview.dureeParsingMs / 1000).toFixed(2)}s</Tag>
                </Space>
            }
        >
            {/* Compteurs principaux */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Statistic title="Polices" value={preview.totalPolices}
                        valueStyle={{ color: "#242166", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Engins" value={preview.totalEngins}
                        valueStyle={{ fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Flottes" value={preview.nbFlottes}
                        valueStyle={{ color: preview.nbFlottes > 0 ? "#fa8c16" : "#8c8c8c", fontSize: 22 }}
                        suffix={<Text type="secondary" style={{ fontSize: 12 }}> multi-engins</Text>}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Période"
                        formatter={() => (
                            <Text style={{ fontSize: 13 }}>
                                {formatDate(preview.dateEffetMin)} → {formatDate(preview.dateEffetMax)}
                            </Text>
                        )}
                    />
                </Col>
            </Row>

            {preview.totalDejaImportes > 0 && (
                <Alert
                    type="info" showIcon
                    icon={<DatabaseOutlined style={{ color: "#9333ea" }} />}
                    style={{ marginBottom: 12 }}
                    message={
                        <span>
                            <Text strong style={{ color: "#7e22ce" }}>{preview.totalDejaImportes}</Text>
                            {" "}police(s)/avenant(s) déjà en base — ignoré(s) à l'import (aucun doublon).
                        </span>
                    }
                />
            )}

            <Divider style={{ margin: "10px 0" }} />

            <Row gutter={16}>
                {/* Répartition opérations */}
                <Col xs={24} md={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Répartition par opération
                    </Text>
                    <Space wrap>
                        {Object.entries(ops).map(([k, v]) => (
                            <Tag key={k} color="blue">
                                {OP_LABELS[k] || k} : <strong>{v}</strong>
                            </Tag>
                        ))}
                        {Object.keys(ops).length === 0 && <Text type="secondary">—</Text>}
                    </Space>
                </Col>

                {/* Répartition catégories */}
                <Col xs={24} md={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Répartition par catégorie
                    </Text>
                    <Space wrap>
                        {Object.entries(cats).map(([k, v]) => (
                            <Tag key={k} color={k === "M" ? "orange" : k === "V" ? "green" : "geekblue"}>
                                {CAT_LABELS[k] || k} : <strong>{v}</strong>
                            </Tag>
                        ))}
                        {Object.keys(cats).length === 0 && <Text type="secondary">—</Text>}
                    </Space>
                </Col>

                {/* Primes */}
                <Col xs={24} md={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Primes
                    </Text>
                    <Descriptions size="small" column={1} bordered={false}>
                        <Descriptions.Item label="Prime nette">{formatMontant(preview.sommePrimeNette)}</Descriptions.Item>
                        <Descriptions.Item label="Prime totale">{formatMontant(preview.sommePrimeTotale)}</Descriptions.Item>
                    </Descriptions>
                </Col>
            </Row>
        </Card>
    );
};

// ─── Page principale ────────────────────────────────────────────────────────

const ReprisePolicesEforce = () => {
    const [compagnies, setCompagnies]                 = useState([]);
    const [selectedCompagnie, setSelectedCompagnie]   = useState(null);
    const [selectedBranche, setSelectedBranche]       = useState(null);
    const [fichiers, setFichiers]                     = useState([]);

    // Étapes : 0 = sélection, 1 = preview, 2 = import, 3 = rapport
    const [step, setStep]                             = useState(0);

    const [previews, setPreviews]                     = useState([]);
    const [previewLoading, setPreviewLoading]         = useState(false);

    const [rapports, setRapports]                     = useState([]);
    const [importLoading, setImportLoading]           = useState(false);
    const [progress, setProgress]                     = useState(0);
    const [currentFile, setCurrentFile]               = useState(null);

    const [precLoading, setPrecLoading]               = useState(false);
    const [precResult, setPrecResult]                 = useState(null);

    const abortRef = useRef(false);

    useEffect(() => {
        axiosInstance.get("/api/organisme/assurance")
            .then(res => setCompagnies(res.data))
            .catch(() => toast.error("Impossible de charger les compagnies"));
    }, []);

    const handleUploadChange = ({ fileList }) => setFichiers(fileList);

    // ── Étape 0 → 1 : Prévisualiser ──────────────────────────────────────────
    const lancerPreview = async () => {
        if (!selectedCompagnie) { toast.warn("Sélectionnez une compagnie"); return; }
        const valides = fichiers.filter(f => f.originFileObj);
        if (valides.length === 0) { toast.warn("Ajoutez au moins un fichier"); return; }

        setPreviewLoading(true);
        setPreviews([]);
        const resultats = [];

        for (const f of valides) {
            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            formData.append("compagnie", selectedCompagnie.code);
            formData.append("branche", selectedBranche);
            formData.append("organismeCodeId", selectedCompagnie.code);

            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-eforce/preview", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push(res.data);
            } catch (err) {
                resultats.push({
                    fichier: f.name, ok: false,
                    messageErreur: err.response?.data?.message || err.message || "Erreur serveur",
                });
            }
        }

        setPreviews(resultats);
        setPreviewLoading(false);
        setStep(1);

        const nbErreurs = resultats.filter(r => !r.ok).length;
        if (nbErreurs > 0)
            toast.warn(`${nbErreurs} fichier(s) en erreur — vérifiez l'aperçu`);
        else
            toast.success("Aperçu généré — vérifiez les statistiques avant d'importer");
    };

    // ── Étape 1 → 2 → 3 : Importer ───────────────────────────────────────────
    const lancerImport = async () => {
        const valides = fichiers.filter(f => f.originFileObj);
        setImportLoading(true);
        abortRef.current = false;
        setRapports([]);
        setProgress(0);
        setStep(2);

        const total = valides.length;
        const resultats = [];

        for (let i = 0; i < total; i++) {
            if (abortRef.current) break;
            const f = valides[i];
            setCurrentFile(f.name);

            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            formData.append("compagnie", selectedCompagnie.code);
            formData.append("branche", selectedBranche);
            formData.append("organismeCodeId", selectedCompagnie.code);

            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-eforce", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push({ fichier: f.name, ...res.data });
            } catch (err) {
                resultats.push({
                    fichier: f.name, ok: false,
                    messageErreurGlobal: err.response?.data?.message || err.message || "Erreur serveur",
                    totalParse: 0, succes: 0, nbErreurs: 0, erreurs: [],
                });
            }
            setProgress(Math.round(((i + 1) / total) * 100));
        }

        setRapports(resultats);
        setImportLoading(false);
        setCurrentFile(null);
        setStep(3);

        const totalSucces  = resultats.reduce((s, r) => s + (r.succes || 0), 0);
        const totalErreurs = resultats.reduce((s, r) => s + (r.nbErreurs || 0), 0);
        toast.success(`Import terminé — ${totalSucces} intégrés, ${totalErreurs} rejetés`);
    };

    const exporterRapport = () => {
        const wb = XLSX.utils.book_new();
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const heureStr = now.toTimeString().slice(0, 8).replace(/:/g, "h").replace(/h(\d{2})$/, "m$1");

        // Nom du fichier : REPRISE_{COMPAGNIE}_{BRANCHE}_{source}_{date}.xlsx
        const slug = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
        const compCode   = slug(selectedCompagnie?.code || "");
        const brancheSlug = slug(selectedBranche || "");
        const sourceSlug  = rapports.length === 1
            ? slug((rapports[0].fichier || "").replace(/\.(xls|xlsx)$/i, ""))
            : `${rapports.length}FICHIERS`;
        const nomFichier = `REPRISE_${compCode}_${brancheSlug}_${sourceSlug}_${dateStr}_${heureStr}.xlsx`;

        // ── Onglet 1 : Résumé par fichier ─────────────────────────────────────
        const resumeRows = [
            ["Fichier", "Parsés", "Intégrés", "Rejetés", "Durée (s)", "Compagnie", "Branche"],
        ];
        rapports.forEach(r => resumeRows.push([
            r.fichier,
            r.totalParse,
            r.succes,
            r.nbErreurs,
            r.dureeMs != null ? (r.dureeMs / 1000).toFixed(1) : "—",
            selectedCompagnie?.code || "—",
            selectedBranche || "—",
        ]));
        const wsResume = XLSX.utils.aoa_to_sheet(resumeRows);
        wsResume["!cols"] = [{ wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
        XLSX.utils.book_append_sheet(wb, wsResume, "Résumé");

        // ── Onglet 2 : Détail des lignes rejetées ─────────────────────────────
        const erreursRows = [
            ["Fichier", "Ligne", "N° Police", "Avenant", "Assuré", "Immatriculation",
             "Marque", "Catégorie", "Nb Places", "Date Effet", "Date Échéance",
             "Prime TTC", "N° Attestation", "Cause du rejet"],
        ];
        rapports.forEach(r => {
            (r.erreurs || []).forEach(e => erreursRows.push([
                r.fichier,
                e.ligne ?? "",
                e.police || "",
                e.avenant || "",
                e.assure || "",
                e.immatriculation || "",
                e.marque || "",
                e.categorie || "",
                e.nbPlaces ?? "",
                e.dateEffet || "",
                e.dateEcheance || "",
                e.primeTotale != null ? Math.round(e.primeTotale) : "",
                e.attestation || "",
                e.erreur || "",
            ]));
        });
        const wsErreurs = XLSX.utils.aoa_to_sheet(erreursRows);
        wsErreurs["!cols"] = [
            { wch: 35 }, { wch: 8 }, { wch: 20 }, { wch: 10 }, { wch: 30 }, { wch: 14 },
            { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
            { wch: 14 }, { wch: 18 }, { wch: 60 },
        ];
        XLSX.utils.book_append_sheet(wb, wsErreurs, "Lignes rejetées");

        XLSX.writeFile(wb, nomFichier);
    };

    const resetAll = () => {
        setStep(0);
        setFichiers([]);
        setPreviews([]);
        setRapports([]);
        setProgress(0);
        setPrecResult(null);
        setSelectedCompagnie(null);
        setSelectedBranche(null);
    };

    const recalculerPrec = async () => {
        setPrecLoading(true);
        setPrecResult(null);
        try {
            const res = await axiosInstance.post("/api/migration/reprise-eforce/recalculer-prec");
            setPrecResult(res.data);
            toast.success(`PREC recalculé pour ${res.data.updated} contrats repris`);
        } catch (e) {
            toast.error("Erreur lors du recalcul PREC");
        } finally {
            setPrecLoading(false);
        }
    };

    // ── Totaux globaux (rapport) ──────────────────────────────────────────────
    const totalParse   = rapports.reduce((s, r) => s + (r.totalParse || 0), 0);
    const totalSucces  = rapports.reduce((s, r) => s + (r.succes || 0), 0);
    const totalErreurs = rapports.reduce((s, r) => s + (r.nbErreurs || 0), 0);

    // ── Totaux preview ────────────────────────────────────────────────────────
    const previewTotalPolices = previews.filter(p => p.ok).reduce((s, p) => s + (p.totalPolices || 0), 0);
    const previewSommePrimes  = previews.filter(p => p.ok).reduce((s, p) => s + (p.sommePrimeTotale || 0), 0);

    const brancheInfo   = BRANCHES.find(b => b.value === selectedBranche);
    const hasEforce     = previews.some(p => p.ok && p.formatDetecte === "EFORCE");

    return (
        <PageLayout currentMenu="REPRISE">
            <div className="container-xxl flex-grow-1 container-p-y">

                {/* En-tête */}
                <div className="mb-4">
                    <Title level={4} style={{ margin: 0, color: "#242166" }}>
                        <DatabaseOutlined className="me-2" />
                        Reprise de données — Polices compagnies
                    </Title>
                    <Text type="secondary">
                        Formats détectés automatiquement · BDE/BDA · Template Émissions · BD-POOL eFORCE
                    </Text>
                </div>

                {/* Stepper */}
                <Card style={{ marginBottom: 24 }}>
                    <Steps
                        current={step}
                        size="small"
                        items={[
                            { title: "Sélection",     icon: <FileExcelOutlined /> },
                            { title: "Aperçu",        icon: previewLoading ? <LoadingOutlined /> : <EyeOutlined /> },
                            { title: "Import",        icon: importLoading ? <LoadingOutlined /> : <RocketOutlined /> },
                            { title: "Rapport final", icon: step === 3 ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : <DatabaseOutlined /> },
                        ]}
                    />
                </Card>

                {/* ── ÉTAPE 0 : Sélection ──────────────────────────────────── */}
                {step === 0 && (
                    <Card title="Paramètres d'import" style={{ borderTop: "3px solid #242166" }}>
                        <Row gutter={[16, 16]}>

                            {/* Compagnie */}
                            <Col xs={24} md={12}>
                                <div className="mb-2"><Text strong>Compagnie</Text></div>
                                <Select
                                    placeholder="Sélectionner la compagnie"
                                    size="large"
                                    style={{ width: "100%" }}
                                    showSearch
                                    filterOption={(input, opt) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(_, opt) => { setSelectedCompagnie(opt.data); setFichiers([]); }}
                                    options={compagnies.map(c => ({ value: c.code, label: c.raisonSociale, data: c }))}
                                />
                            </Col>

                            {/* Branche */}
                            <Col xs={24} md={12}>
                                <div className="mb-2">
                                    <Text strong>Branche</Text>
                                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>(optionnel pour BD-POOL eFORCE)</Text>
                                </div>
                                <Select
                                    placeholder="Sélectionner la branche"
                                    size="large"
                                    style={{ width: "100%" }}
                                    value={selectedBranche}
                                    allowClear
                                    onChange={val => setSelectedBranche(val || null)}
                                    options={BRANCHES.map(b => ({
                                        value: b.value,
                                        label: b.label,
                                    }))}
                                />
                                {brancheInfo ? (
                                    <div className="mt-1">
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {brancheInfo.description}
                                        </Text>
                                    </div>
                                ) : (
                                    <div className="mt-1">
                                        <Text style={{ fontSize: 11, color: "#1d4ed8" }}>
                                            Pour le format BD-POOL eFORCE, les catégories sont lues directement depuis le fichier (col. Catégorie) — la branche sélectionnée ici est ignorée.
                                        </Text>
                                    </div>
                                )}
                            </Col>

                            {/* Fichiers */}
                            <Col xs={24}>
                                <div className="mb-2">
                                    <Text strong>Fichiers</Text>
                                    <Text type="secondary" className="ms-2">(plusieurs fichiers acceptés)</Text>
                                    <Tag color="default" className="ms-2" style={{ fontSize: 11 }}>
                                        Format détecté automatiquement
                                    </Tag>
                                </div>
                                <Dragger
                                    onChange={handleUploadChange}
                                    beforeUpload={() => false}
                                    accept=".xls,.xlsx"
                                    multiple
                                    fileList={fichiers}
                                    disabled={!selectedCompagnie}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{
                                            fontSize: 36,
                                            color: selectedCompagnie ? "#242166" : "#d9d9d9"
                                        }} />
                                    </p>
                                    <p className="ant-upload-text">
                                        {selectedCompagnie
                                            ? "Glissez les fichiers ici ou cliquez"
                                            : "Sélectionnez d'abord une compagnie"}
                                    </p>
                                    <p className="ant-upload-hint text-muted">
                                        Fichiers .xls ou .xlsx — le format (BDE, eFORCE, Template) sera détecté automatiquement
                                    </p>
                                </Dragger>
                            </Col>
                        </Row>

                        {/* Info formats */}
                        <Divider orientation="left" style={{ marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Formats supportés</Text>
                        </Divider>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            <Tag color="orange" style={{ fontSize: 11 }}>BDE/BDA · 27 col. · .xls · SANLAM, GTA, ALLIANZ…</Tag>
                            <Tag color="green"  style={{ fontSize: 11 }}>Template Émissions · 22 col. · .xlsx · FIDELIA, OSSAN</Tag>
                            <Tag color="blue"   style={{ fontSize: 11 }}>BD-POOL eFORCE · 54 col. · .xls · NSIA, SUNU, OGAR…</Tag>
                        </div>

                        <div className="d-flex justify-content-end mt-4">
                            <Button
                                type="primary"
                                size="large"
                                icon={<EyeOutlined />}
                                loading={previewLoading}
                                onClick={lancerPreview}
                                style={{ backgroundColor: "#242166", minWidth: 200 }}
                                disabled={!selectedCompagnie || fichiers.filter(f => f.originFileObj).length === 0}
                            >
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 1 : Aperçu ─────────────────────────────────────── */}
                {step === 1 && (
                    <>
                        {/* Synthèse globale */}
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #242166" }}>
                            <Row gutter={16} align="middle">
                                <Col xs={24} sm={8}>
                                    <Statistic
                                        title="Total polices détectées"
                                        value={previewTotalPolices}
                                        valueStyle={{ color: "#242166", fontSize: 28 }}
                                        suffix={<Text type="secondary" style={{ fontSize: 14 }}> sur {fichiers.length} fichier(s)</Text>}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Statistic
                                        title="Somme primes totales"
                                        value={formatMontant(previewSommePrimes)}
                                        valueStyle={{ fontSize: 20 }}
                                        formatter={v => v}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">Compagnie / Branche</Text>
                                        <Tag color="#242166" style={{ fontSize: 13 }}>
                                            {selectedCompagnie?.raisonSociale} ({selectedCompagnie?.code})
                                        </Tag>
                                        {hasEforce ? (
                                            <Tag color="cyan">Multibranches — catégories du fichier</Tag>
                                        ) : brancheInfo ? (
                                            <Tag color={brancheInfo.color}>{brancheInfo.label}</Tag>
                                        ) : null}
                                    </Space>
                                </Col>
                            </Row>
                        </Card>

                        {/* Alert eFORCE multibranches */}
                        {hasEforce && (
                            <Alert
                                type="info" showIcon
                                message="Format BD-POOL eFORCE — fichier multibranches"
                                description="Les catégories sont lues directement depuis la colonne « Catégorie » du fichier. La branche sélectionnée n'est pas appliquée pour ces fichiers."
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {/* Détail par fichier */}
                        <Card
                            title={
                                <Space>
                                    <EyeOutlined />
                                    <Text strong>Aperçu par fichier</Text>
                                    <Badge count={previews.length} color="#242166" />
                                </Space>
                            }
                            style={{ marginBottom: 16 }}
                        >
                            {previews.map((p, i) => <PreviewCard key={i} preview={p} />)}
                        </Card>

                        {/* Tableau des lignes parsées */}
                        {(() => {
                            const toutesLignes = previews.flatMap(p => p.lignes || []);
                            const nbWarnings   = toutesLignes.filter(l => l._hasWarning).length;
                            const nbDejaImportes = toutesLignes.filter(l => l._dejaImporte).length;
                            if (toutesLignes.length === 0) return null;
                            return (
                                <Card
                                    style={{ marginBottom: 16, padding: 0 }}
                                    bodyStyle={{ padding: 0 }}
                                    title={
                                        <Space>
                                            <DatabaseOutlined />
                                            <Text strong>Lignes à importer</Text>
                                            <Tag color="#242166">{toutesLignes.length} polices</Tag>
                                            {nbDejaImportes > 0 && (
                                                <Tag color="purple">{nbDejaImportes} déjà en base</Tag>
                                            )}
                                            {nbWarnings > 0 && (
                                                <Tag color="orange" icon={<WarningOutlined />}>
                                                    {nbWarnings} alerte(s)
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                >
                                    {nbDejaImportes > 0 && (
                                        <Alert
                                            type="info" showIcon icon={<DatabaseOutlined style={{ color: "#9333ea" }} />}
                                            message={`${nbDejaImportes} ligne(s) déjà présente(s) en base (même police + dates + avenant + organisme)`}
                                            description="Ces lignes seront automatiquement IGNORÉES à l'import (aucun doublon créé). Repérées par le badge violet « déjà »."
                                            style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none" }}
                                        />
                                    )}
                                    {nbWarnings > 0 && (
                                        <Alert
                                            type="warning" showIcon icon={<WarningOutlined />}
                                            message={`${nbWarnings} ligne(s) avec alertes (données incomplètes)`}
                                            description="Ces lignes seront quand même importées avec les données disponibles. Les champs manquants apparaissent en rouge."
                                            style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none" }}
                                        />
                                    )}
                                    <Table
                                        dataSource={toutesLignes.map((r, i) => ({ ...r, key: i }))}
                                        columns={previewLignesColumns}
                                        size="small"
                                        scroll={{ x: 1300, y: 440 }}
                                        pagination={{ pageSize: 50, showTotal: t => `${t} polices`, showSizeChanger: false }}
                                        rowClassName={r => r._dejaImporte ? "preview-row-deja" : (r._hasWarning ? "preview-row-warning" : "")}
                                    />
                                </Card>
                            );
                        })()}

                        {/* Actions */}
                        <div className="d-flex justify-content-between">
                            <Button size="large" onClick={() => setStep(0)}>
                                ← Modifier la sélection
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                icon={<RocketOutlined />}
                                onClick={lancerImport}
                                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", minWidth: 220 }}
                                disabled={previews.every(p => !p.ok)}
                            >
                                Confirmer et importer
                            </Button>
                        </div>
                    </>
                )}

                {/* ── ÉTAPE 2 : Import en cours ───────────────────────────── */}
                {step === 2 && (
                    <Card style={{ borderTop: "3px solid #52c41a" }}>
                        <div style={{ textAlign: "center", padding: "24px 0" }}>
                            <LoadingOutlined style={{ fontSize: 40, color: "#242166", marginBottom: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 16 }}>Import en cours…</Text>
                            </div>
                            <Text type="secondary">
                                Fichier : <Text strong>{currentFile}</Text>
                                &nbsp;({fichiers.findIndex(f => f.name === currentFile) + 1}/{fichiers.length})
                            </Text>
                            <Progress
                                percent={progress}
                                strokeColor="#242166"
                                style={{ marginTop: 16, maxWidth: 500, margin: "16px auto 0" }}
                            />
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 3 : Rapport final ──────────────────────────────── */}
                {step === 3 && (
                    <>
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #52c41a" }}>
                            <Row gutter={16}>
                                <Col xs={8}>
                                    <Statistic title="Lignes parsées" value={totalParse}
                                        valueStyle={{ color: "#1890ff" }} />
                                </Col>
                                <Col xs={8}>
                                    <Statistic title="Intégrées" value={totalSucces}
                                        valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleFilled />} />
                                </Col>
                                <Col xs={8}>
                                    <Statistic title="Rejetées" value={totalErreurs}
                                        valueStyle={{ color: totalErreurs > 0 ? "#ff4d4f" : "#52c41a" }}
                                        prefix={totalErreurs > 0 ? <CloseCircleFilled /> : <CheckCircleFilled />}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        <Card
                            title={
                                <Space>
                                    <Text strong>Rapport d'import</Text>
                                    <Badge count={rapports.length} color="#242166" />
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Button icon={<DownloadOutlined />} onClick={exporterRapport}>Exporter</Button>
                                    <Button onClick={resetAll}>Nouvelle reprise</Button>
                                </Space>
                            }
                        >
                            {rapports.map((r, idx) => (
                                <div key={idx} style={{ marginBottom: 20 }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <Space>
                                            <FileExcelOutlined style={{ color: "#217346" }} />
                                            <Text strong>{r.fichier}</Text>
                                        </Space>
                                        <Space>
                                            <Tag color="blue">{r.totalParse} parsés</Tag>
                                            <Tag color="green">{r.succes} intégrés</Tag>
                                            {r.nbErreurs > 0
                                                ? <Tag color="red">{r.nbErreurs} rejetés</Tag>
                                                : <Tag color="green">Aucun rejet</Tag>
                                            }
                                            {r.dureeMs && <Tag color="default">{(r.dureeMs / 1000).toFixed(1)}s</Tag>}
                                        </Space>
                                    </div>

                                    {!r.ok && r.messageErreurGlobal && (
                                        <Alert type="error" message="Échec"
                                            description={r.messageErreurGlobal} showIcon style={{ marginBottom: 8 }} />
                                    )}

                                    {r.erreurs && r.erreurs.length > 0 && (
                                        <Table
                                            size="small"
                                            columns={erreurColumns}
                                            dataSource={r.erreurs.map((e, i) => ({ ...e, key: i }))}
                                            pagination={{ pageSize: 10, size: "small" }}
                                            scroll={{ x: true }}
                                        />
                                    )}

                                    {idx < rapports.length - 1 && <Divider style={{ margin: "12px 0" }} />}
                                </div>
                            ))}
                        </Card>

                        {/* ── Recalcul PREC post-migration ──────────────────── */}
                        <Card
                            style={{ marginTop: 16, borderTop: "3px solid #faad14" }}
                            title={
                                <Space>
                                    <RocketOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Post-traitement PREC</Text>
                                </Space>
                            }
                        >
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Text type="secondary">
                                    Le PREC n'est pas calculé pendant la migration pour optimiser la vitesse.
                                    Cliquer sur le bouton ci-dessous pour le recalculer sur l'ensemble des contrats repris.
                                </Text>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={precLoading ? <LoadingOutlined /> : <RocketOutlined />}
                                        loading={precLoading}
                                        onClick={recalculerPrec}
                                        style={{ background: "#faad14", borderColor: "#faad14" }}
                                    >
                                        Recalculer le PREC (tous contrats repris)
                                    </Button>
                                    {precResult && (
                                        <Tag color="green" style={{ fontSize: 13, padding: "4px 10px" }}>
                                            <CheckCircleFilled style={{ marginRight: 6 }} />
                                            {precResult.updated} contrats mis à jour
                                            {precResult.dureeMs && ` en ${(precResult.dureeMs / 1000).toFixed(1)}s`}
                                        </Tag>
                                    )}
                                </Space>
                            </Space>
                        </Card>
                    </>
                )}

            </div>

            <style>{`
                .preview-row-warning td { background: #fff7ed !important; }
                .preview-row-warning:hover td { background: #fef3c7 !important; }
                .preview-row-deja td { background: #faf5ff !important; color: #6b7280; }
                .preview-row-deja:hover td { background: #f3e8ff !important; }
            `}</style>
        </PageLayout>
    );
};

export default ReprisePolicesEforce;
